<<template>
  <v-expansion-panels v-model="expand" flat outlined>
    <v-expansion-panel>
      <v-expansion-panel-header class="px-3">
        <v-card flat>
          <v-card-title class="pa-0">
            <v-icon class="me-2" color="orange">mdi mdi-information</v-icon>

            {{ assignments.examPeriodName }} | {{ assignments.examScheduleName }} |
            {{ assignments.studyYear }} |
            {{ $t("invigilatorAssignmentsSummary.round") }}
            {{ getEnumLabel("examRound", assignments.round) }}
          </v-card-title>
        </v-card>
      </v-expansion-panel-header>

      <v-expansion-panel-content>
        <v-card class="bulk-report-wrapper mb-4" :class="{ 'dark-mode': isDark }">
          <table class="info-bar-table" style="margin-bottom: 20px;">
            <tr>
              <td class="lbl-gray">{{ $t("invigilatorAssignmentsSummary.totalAssignments") }}:</td>

              <td class="val-text" style="font-weight: bold;">
                {{ assignments.totalAssignments }}
                {{ $t("invigilatorAssignmentsSummary.assignment") }}
              </td>

              <td class="lbl-gray">{{ $t("invigilatorAssignmentsSummary.hallManager") }}:</td>

              <td class="val-text">
                {{ assignments.hallManagerCount }}
                {{ $t("invigilatorAssignmentsSummary.times") }}
              </td>

              <td class="lbl-gray">{{ $t("invigilatorAssignmentsSummary.invigilator") }}:</td>

              <td class="val-text">
                {{ assignments.invigilatorCount }}
                {{ $t("invigilatorAssignmentsSummary.times") }}
              </td>
            </tr>
          </table>

          <table class="simple-print-table">
            <thead>
              <tr>
                <th style="width: 15%">
                  {{ $t("invigilatorAssignmentsSummary.dayAndDate") }}
                </th>

                <th style="width: 15%">
                  {{ $t("invigilatorAssignmentsSummary.timePeriod") }}
                </th>

                <th style="width: 15%">
                  {{ $t("invigilatorAssignmentsSummary.examHall") }}
                </th>

                <th style="width: 30%">
                  {{ $t("invigilatorAssignmentsSummary.subjectDepartmentStage") }}
                </th>

                <th style="width: 13%">
                  {{ $t("invigilatorAssignmentsSummary.examType") }}
                </th>

                <th style="width: 12%">
                  {{ $t("invigilatorAssignmentsSummary.roleTask") }}
                </th>
              </tr>
            </thead>

            <tbody>
              <template v-if="assignments.dailyAssignments && assignments.dailyAssignments.length">
                <template v-for="day in assignments.dailyAssignments">
                  <tr v-for="(session, sIdx) in day.sessions" :key="session.assignmentGuid">
                    <td v-if="sIdx === 0" :rowspan="day.sessions.length" class="date-cell">
                      <div class="day-name">
                        {{ formatDate(day.examDate, "dddd") }}
                      </div>

                      <div class="date-sub">
                        {{ day.examDate|moment("YYYY/MM/DD") }}
                      </div>
                    </td>

                    <td >
                      {{ formatTime(session.startTime) }} -
                      {{ formatTime(session.endTime) }}
                    </td>

                    <td>{{ session.hallName }}</td>

                    <td style="padding-right: 10px;">
                      {{ session.subjectName }}
                      ({{ session.departmentName }} - {{ session.stageName }})
                    </td>

                    <td :class="getExamTypeClass(session.examType)" :title="getExamTypeText(session.examType)">
                      {{ getEnumLabel("examType", session.examType) }}
                    </td>

                    <td>
                      <span v-if="session.role === 2" class="text-bold-manager">
                        {{ $t("invigilatorAssignmentsSummary.hallSupervisor") }}
                      </span>

                      <span v-else>
                        {{ $t("invigilatorAssignmentsSummary.invigilator") }}
                      </span>
                      <span v-if="session.isReserve" class="text-reserve-orange">
                        / {{ $t("invigilatorAssignmentsSummary.reserve") }}
                      </span>
                    </td>
                  </tr>
                </template>
              </template>

              <tr v-else>
                <td colspan="6" class="no-data">
                  {{ $t("invigilatorAssignmentsSummary.noAssignments") }}
                </td>
              </tr>
            </tbody>
          </table>
        </v-card>
      </v-expansion-panel-content>
    </v-expansion-panel>
  </v-expansion-panels>
</template>

<script>
export default {
  props: ["assignments"],

  data() {
    return {
      expand: 0
    };
  },

  computed: {
    isDark() {
      return this.$vuetify.theme.dark;
    },
    lang() {
      return this.$i18n.locale;
    }
  },

  methods: {
    formatDate(dateStr, format) {
      const date = new Date(dateStr);

      if (format === "dddd") {
        return date.toLocaleDateString(this.lang == "ar" ? "ar" : "en", {
          weekday: "long"
        });
      }

      return date.toLocaleDateString("en-GB").replace(/\//g, "/");
    },

     formatTime(timeStr) {
      if (!timeStr) return "";
      const date = new Date(timeStr);
      return date.toLocaleTimeString(this.lang == "ar" ? "ar" : "en", {
        hour: "2-digit",
        minute: "2-digit"
      });
    },

    getEnumLabel(type, value) {
      const map = {
        examType: {
          1: this.$t("invigilatorAssignmentsSummary.assessment"),
          2: this.$t("invigilatorAssignmentsSummary.joint"),
          3: this.$t("invigilatorAssignmentsSummary.normal")
        },

        invigilatorRole: {
          1: this.$t("invigilatorAssignmentsSummary.invigilator"),
          2: this.$t("invigilatorAssignmentsSummary.hallSupervisor")
        },

        invigilatorJobType: {
          1: this.$t("invigilatorAssignmentsSummary.permanent"),
          2: this.$t("invigilatorAssignmentsSummary.contract"),
          3: this.$t("invigilatorAssignmentsSummary.daily"),
          4: this.$t("invigilatorAssignmentsSummary.externalLecturer")
        },

        scientificDegrees: {
          1: this.$t("invigilatorAssignmentsSummary.bachelor"),
          2: this.$t("invigilatorAssignmentsSummary.master"),
          3: this.$t("invigilatorAssignmentsSummary.phd")
        },

        examRound: {
          1: this.$t("invigilatorAssignmentsSummary.first"),
          2: this.$t("invigilatorAssignmentsSummary.second"),
          3: this.$t("invigilatorAssignmentsSummary.third"),
          4: this.$t("invigilatorAssignmentsSummary.fourth")
        }
      };

      return map[type][value] || value;
    },

    getExamTypeClass(type) {
      if (type === 1) return "bg-assessment";
      if (type === 2) return "bg-joint";

      return "";
    },
     getExamTypeText(type) {
      if (type === 1) return "Assessment";
      if (type === 2) return "Joint";
      return "";
    }
  }
};
</script>

<style scoped>
/* Theme Variables Mapping */
.bulk-report-wrapper {
  --app-bg: #ffffff;
  --header-text: #000000;
  --table-border: #111111;
  --label-bg: #eaeaea;
  --val-bg: #ffffff;
  --text-muted: #555555;
  --row-alt: #fafafa;
  --manager-color: #4a148c;
  --reserve-color: #e65100;

  font-family: "Segoe UI", Tahoma, Arial, sans-serif;
  width: 100%;
  max-width: 1100px;
  margin: 0 auto;
  padding: 20px;
  background-color: var(--app-bg);
  transition: background 0.3s ease;
}

/* Dark Mode Integration */
.bulk-report-wrapper.dark-mode {
  --app-bg: #23272f; /* highlight */
  --header-text: #f3f4f6; /* grey-1 */
  --table-border: #4b5563; /* grey-4 */
  --label-bg: #2d323b; /* highlight-1 */
  --val-bg: #23272f;
  --text-muted: #9ca3af; /* grey-4 */
  --row-alt: #2d323b;
  --manager-color: #ec407a; /* secondary color from your theme */
  --reserve-color: #109afd; /* primary color from your theme */
}

/* Header Styles */
.schedule-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  border: 1px solid var(--table-border);
  border-bottom: none;
  padding: 15px 25px;
}

.header-logo {
  width: 80px;
  height: auto;
  filter: grayscale(0.2);
}

.header-titles {
  text-align: center;
  flex-grow: 1;
}

.header-titles h6 {
  margin: 4px 0;
  font-weight: bold;
  color: var(--header-text);
  font-size: 0.95rem;
}

/* Tables Styles */
.info-bar-table,
.simple-print-table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: -1px; /* Prevent double borders between header and table */
}

.info-bar-table td,
.simple-print-table th,
.simple-print-table td {
  border: 1px solid var(--table-border);
  padding: 8px 12px;
  font-size: 13px;
  color: var(--header-text);
  vertical-align: middle;
  text-align: center;
}

.lbl-gray {
  background-color: var(--label-bg) !important;
  font-weight: bold;
  width: 14%;
}

.val-text {
  background-color: var(--val-bg);
  width: 19%;
}

.simple-print-table th {
  background-color: var(--label-bg) !important;
  font-weight: bold;
  text-align: center;
}

.simple-print-table tr:nth-child(even) td {
  background-color: var(--row-alt);
}

/* Specific Cells */
.date-cell {
  background-color: var(--val-bg) !important;
  font-weight: bold;
}

.day-name {
  font-size: 14px;
}
.date-sub {
  font-size: 14px;
  color: var(--text-muted);
  margin-top: 3px;
  font-weight: bold;
}

.time-cell {
  font-family: "Courier New", Courier, monospace;
  font-weight: bold;
  font-size: 12px;
}

/* Status Colors */
.text-bold-manager {
  font-weight: bold;
  color: var(--manager-color);
}
.text-reserve-orange {
  font-weight: bold;
  color: var(--reserve-color);
}

.bg-assessment {
  background-color: #93c47d !important; /* Fixed Green per requirements */
  color: #000 !important;
}

.bg-joint {
  background-color: #a2c4c9 !important; /* Fixed Light Blue per requirements */
  color: #000 !important;
}

.empty-alert {
  text-align: center;
  padding: 50px;
  border: 2px dashed var(--table-border);
  color: var(--text-muted);
}
</style>
